import { create } from "zustand";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import axiosInstance from "../../../../Hooks/axoisInstance";
import { API_BASE_URL1 } from "../../../Services/baseApiUrl";

const COLONY_API = {
  GET_ALL: `${API_BASE_URL1}MasterDetails/GetAllColonies`,
  ADD: `${API_BASE_URL1}MasterCreate/AddNewColony`,
  UPDATE: `${API_BASE_URL1}MasterCreate/UpdateColonyDetails`,
};

const pickFirstDefined = (...values) =>
  values.find((v) => v !== undefined && v !== null && String(v).trim() !== "");

const mapColonyData = (colonyList) =>
  colonyList.map((colony) => ({
    ...colony,
    zone: pickFirstDefined(colony.zoneName, colony.ZoneName, colony.zone) || "Unknown",
    ward:
      pickFirstDefined(colony.circleName, colony.CircleName, colony.ward) || "Unknown",
    colony: pickFirstDefined(colony.colonyName, colony.ColonyName, colony.colony) || "",
    colonyName: pickFirstDefined(colony.colonyName, colony.ColonyName, colony.colonyName) || "",
    colonyCode:
      pickFirstDefined(
        colony.colonyCode,
        colony.ColonyCode,
        colony.colonycode,
        colony.colony_code
      ) || "-",
    status: colony.isActive ? "Active" : "InActive",
  }));

const getErrorMessage = (data, fallback) => {
  if (typeof data === "string" && data) return data;
  if (data?.message) return data.message;
  return fallback;
};

const getUserIdFromToken = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return 0;
    const decoded = jwtDecode(token);
    return decoded?.UserId || 0;
  } catch {
    return 0;
  }
};

export const useColonyStore = create((set, get) => ({
  colonies: [],
  filteredColonies: [],
  isLoading: false,
  isSaving: false,
  error: "",
  filters: {
    zoneId: "",
    IsActive: "",
    searchTerm: "",
  },

  setFilters: (filters) => set({ filters }),

  updateFilter: (name, value) =>
    set((state) => ({
      filters: { ...state.filters, [name]: value },
    })),

  applyFilters: (data, currentFilters) => {
    const filters = currentFilters ?? get().filters;
    let result = [...(data ?? get().colonies)];

    if (filters.zoneId) {
      result = result.filter(
        (item) => Number(item.zoneId) === Number(filters.zoneId)
      );
    }

    if (filters.IsActive !== "") {
      const isActiveFilter = filters.IsActive === "true";
      result = result.filter((item) => item.isActive === isActiveFilter);
    }

    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      result = result.filter(
        (item) =>
          item.colonyName?.toLowerCase().includes(term) ||
          item.colonyCode?.toLowerCase().includes(term)
      );
    }

    set({ filteredColonies: result });
    return result;
  },

  fetchColonies: async () => {
    set({ isLoading: true, error: "", colonies: [] });
    try {
      const response = await axiosInstance.get(COLONY_API.GET_ALL, {
        params: { pageNumber: 1, pageSize: 100000 },
      });
      const colonyList = Array.isArray(response?.data)
        ? response.data
        : response?.data?.data || [];
      const mappedData = mapColonyData(colonyList).sort((a, b) => {
        if (a.isActive === b.isActive) return 0;
        return a.isActive ? -1 : 1;
      });
      set({ colonies: mappedData });
      get().applyFilters(mappedData, get().filters);
      return { success: true, data: mappedData };
    } catch (error) {
      const message = getErrorMessage(
        error?.response?.data,
        "Failed to load colony data."
      );
      set({ colonies: [], filteredColonies: [], error: message });
      toast.error(message);
      return { success: false, message };
    } finally {
      set({ isLoading: false });
    }
  },

  saveColony: async (payload, isEdit = false) => {
    set({ isSaving: true, error: "" });
    try {
      let requestPayload = payload;

      if (!isEdit) {
        const userId = getUserIdFromToken();
        if (!userId) {
          const message = "User session expired. Please login again.";
          toast.error(message);
          return { success: false, message };
        }
        requestPayload = { ...payload, userId: userId || 1 };
      }

      const response = isEdit
        ? await axiosInstance.put(COLONY_API.UPDATE, requestPayload)
        : await axiosInstance.post(COLONY_API.ADD, requestPayload);

      const message = getErrorMessage(
        response?.data,
        isEdit ? "Colony updated successfully" : "Colony created successfully."
      );
      toast.success(message);
      await get().fetchColonies();
      return response;
    } catch (error) {
      const message = getErrorMessage(
        error?.response?.data,
        "Unable to save colony details."
      );
      set({ error: message });
      toast.error(message);
      return { success: false, message };
    } finally {
      set({ isSaving: false });
    }
  },
}));
